import React, { useState } from 'react';
import { Icon, Modal } from 'Components';
import { useTranslation } from 'react-i18next';
import LMSButton from 'LearningManager/components/Button';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import UserCountError from 'LearningManager/components/UserCountError';
import useAssignmentsUserCount, {
	ResolveAssignmentUsers
} from 'LearningManager/hooks/useAssignmentsUserCount';
import styles from './CourseUnassignModal.module.scss';
import { pluralLabel } from 'LearningManager/helpers/role';

export interface CourseUnassignAssignmentOption {
	id: number;
	label: string;
}

interface CourseUnassignModalProps {
	visible: boolean;
	options: CourseUnassignAssignmentOption[];
	resolveAssignmentUsers: ResolveAssignmentUsers;
	onCancel: () => void;
	onConfirm: (assignmentIds: number[]) => void;
}

const CourseUnassignModal: React.FC<CourseUnassignModalProps> = ({
	visible,
	options,
	resolveAssignmentUsers,
	onCancel,
	onConfirm
}) => {
	const { t } = useTranslation();

	const [selectedIds, setSelectedIds] = useState<number[]>([]);

	const hasSelection = selectedIds.length > 0;

	const {
		count: userCount,
		failedCount,
		retry
	} = useAssignmentsUserCount(
		selectedIds,
		resolveAssignmentUsers,
		visible
	);

	const handleCancel = () => {
		setSelectedIds([]);
		onCancel();
	};

	const handleConfirm = () => {
		onConfirm(selectedIds);
		setSelectedIds([]);
	};

	return (
		<Modal
			visible={visible}
			title={t('learning.courseAssignments.courseUnassignModal.title')}
			onCancel={handleCancel}
			size="sm"
			destroyOnClose
			className={styles.modal}
			footer={
				<div className={styles.footer}>
					<LMSButton
						size="small"
						variant="borderless"
						type="default"
						onClick={handleCancel}
					>
						{t('learning.courseAssignments.courseUnassignModal.cancel')}
					</LMSButton>

					<LMSButton
						type="primary"
						size="small"
						disabled={!hasSelection || !userCount}
						onClick={handleConfirm}
					>
						{t('learning.courseAssignments.courseUnassignModal.unassign')}
					</LMSButton>
				</div>
			}
		>
			<div className={styles.body}>
				<p className={styles.text}>
					{t(
						'learning.courseAssignments.courseUnassignModal.selectAssignments'
					)}
				</p>

				<div className={styles.field}>
					<div className={styles.field__label}>
						{t(
							'learning.courseAssignments.courseUnassignModal.assignment'
						)}
					</div>

					<MultiSelectDropdown
						options={options}
						placeholder={t(
							'learning.courseAssignments.courseUnassignModal.selectAssignment'
						)}
						defaultSelectedIds={[]}
						onChange={setSelectedIds}
						allLabel={t(
							'learning.courseAssignments.courseUnassignModal.allAssignments'
						)}
						showSelectedLabels
						className={styles.dropdown}
					/>
				</div>

				{hasSelection && (
					<div className={styles.summary}>
						<p className={styles.text}>
							{t(
								'learning.courseAssignments.courseUnassignModal.description'
							)}
						</p>

						{failedCount > 0 ? (
							<UserCountError
								failedCount={failedCount}
								noun={t(
									'learning.courseAssignments.courseUnassignModal.learner'
								)}
								onRetry={retry}
							/>
						) : (
							<ul className={styles.list}>
								<li>
									{t(
										'learning.courseAssignments.courseUnassignModal.affect'
									)}{' '}
									{userCount === undefined ? (
										<Icon
											type="loading"
											aria-label={t(
												'learning.courseAssignments.courseUnassignModal.countingUsers'
											)}
										/>
									) : (
										t(
											pluralLabel('learning.courseAssignments.courseUnassignModal.learners', userCount),
											{
												count: userCount
											}
										)
									)}{' '}
									{t(
										'learning.courseAssignments.courseUnassignModal.total'
									)}
								</li>
							</ul>
						)}
						<div className={styles.warning}>
							<Icon type="warningFilledIcon" role="img" />
							{t(
								'learning.courseAssignments.courseUnassignModal.warning'
							)}
						</div>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default CourseUnassignModal;